import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'src/components/ui/skeleton';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useState } from 'react';
import { useDeleteClientUserMutation, useGetOrganizationClientsQuery } from 'src/pages/client-management/endpoints/client.endpoints';
import trashIcon from '../../../assets/icons/blackTrash.svg';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/redux-store';
import { Client } from 'src/pages/client-management/types/client.types';
import { showNotification } from 'src/redux/notification/notification.slice';
import { useDispatch } from 'react-redux';

interface ClientUsersTableProps {
  client: Client;
  showActionsColumn?: boolean;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  is_client_admin: boolean;
}

export const ClientUsersTable = ({ client, showActionsColumn = true }: ClientUsersTableProps) => {

  const dispatch = useDispatch();
  const [deleteClientUser] = useDeleteClientUserMutation();
  const { refetch: refetchClientsList } = useGetOrganizationClientsQuery();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);
  const currentUser = useSelector((state: RootState) => state.userState);

  const roleTemplate = (rowData: UserData) => {
    if (rowData.is_client_admin) {
      return (
        <span
          className="rounded-full px-2 py-1 text-sm font-normal"
          style={{ background: '#E6F9ED', color: '#238551' }}
        >
          Admin
        </span>
      );
    } else {
      return (
        <span className="bg-blue-100 text-blue-700 rounded-full px-2 py-1 text-sm font-normal">
          User
        </span>
      );
    }
  };

  const handleDeleteClick = (user: UserData) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async() => {
    if (userToDelete) {
      try {
        await deleteClientUser({ clientUserId: userToDelete.id }).unwrap();
        setShowDeleteDialog(false);
        setUserToDelete(null);
        await refetchClientsList();
        dispatch(showNotification({
          summary: 'User deleted',
          theme: 'success',
        }));
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const actionsTemplate = (rowData: UserData) => {
    if (rowData.id === currentUser.extUserId) {
      return null;
    }

    return (
      <div className="flex justify-center">
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-rounded p-button-danger"
          onClick={() => handleDeleteClick(rowData)}
        />
      </div>
    );
  };

  if (client.users === undefined) {
    return (
      <Skeleton className="h-[150px] w-full" />
    );
  }

  if (!client.users?.length) {
    return (
      <div className="text-gray-500 pl-2">
        No users.
      </div>
    );
  }

  return (
    <>
      <DataTable
        value={client.users}
        className="mt-2"
        size="small"
        stripedRows
      >
        {/* <Column
          field="name"
          header="Name"
          sortable
          headerClassName="text-black"
          headerStyle={{ background: 'rgb(246, 246, 246)' }}
        /> */}
        <Column
          field="email"
          header="Email"
          sortable
          headerClassName="text-black"
          headerStyle={{ background: 'rgb(246, 246, 246)' }}
        />
        <Column
          field="is_client_admin"
          header="Role"
          body={roleTemplate}
          sortable
          headerClassName="text-black"
          headerStyle={{ background: 'rgb(246, 246, 246)' }}
        />
        {showActionsColumn && (
          <Column
            header="Actions"
            body={actionsTemplate}
            style={{ width: '80px' }}
            headerClassName="text-black"
            headerStyle={{ background: 'rgb(246, 246, 246)' }}
          />
        )}
      </DataTable>

      <Dialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        style={{ width: '90vw', maxWidth: '460px' }}
        footer={(
          <div className="flex flex-col justify-center mb-2">
            <button
              onClick={handleDeleteConfirm}
              className="p-2 mb-2 px-4 bg-black text-white shadow-md rounded-3xl"
            >
              Remove
            </button>
            <button
              onClick={() => setShowDeleteDialog(false)}
              className="p-2 px-4 bg-white rounded-3xl"
            >
              Cancel
            </button>
          </div>
        )}
        draggable={false}
        resizable={false}
        closable={false}
        header={null}
      >
        <div className="flex flex-col items-center p-3 py-0 overflow-hidden">
          <img
            className="w-6"
            src={trashIcon}
            alt="icon"
          />
          <div className="mt-4 text-xl sm:text-3xl text-black text-center">
            Are you sure you want to remove
            {' '}
            {(userToDelete?.name && userToDelete.name !== '-') ? userToDelete.name : userToDelete?.email}
            ?
          </div>
        </div>
      </Dialog>
    </>
  );
};